import React, { ChangeEvent, Component, FunctionComponent } from "react";
import { generateUUID } from "../../../utils/uid";
import { Minus, Plus } from "@vitalsource/vstui-icons";
import styled, { css } from "styled-components";
import { cloud, ninja } from "@vitalsource/vstui/colors";
import { clamp } from "../../../utils/number";
import {
  FormattedMessage,
  injectIntl,
  WrappedComponentProps,
} from "react-intl";

const TRACK_SIZE = 2;
const THUMB_SIZE = 12;
const HALF_SPACING = 8;

const styles = {
  trackStyles: css`
    height: ${TRACK_SIZE}px;
    border-radius: ${TRACK_SIZE}px;
    background-color: rgba(0, 0, 0, 0.2);
  `,
  thumbStyles: css`
    appearance: none;
    height: ${THUMB_SIZE}px;
    width: ${THUMB_SIZE}px;
    border-radius: ${THUMB_SIZE}px;
    background: white;
    box-shadow: 0 0 0 0.5px rgba(0, 0, 0, 0.1),
      0 0.5px 1px 0 rgba(0, 0, 0, 0.27);
  `,
};

const Styled = {
  RangeInput: styled.input`
    appearance: none;
    padding: 0 ${HALF_SPACING}px;
    width: 100%;
    height: 28px;
    margin: 0;
    border: 0;
    cursor: pointer;
    &:focus {
      border: 0;
      border-radius: 2px;
      box-shadow: 0 0 0 2px
        ${props => props.theme.vstui.color.primary.getShade(6)};
      outline: none;
    }
    /* These get cranky if you use shared declarations, so reusing vars instead */
    &::-webkit-slider-runnable-track {
      ${styles.trackStyles}
    }
    &::-moz-range-track {
      ${styles.trackStyles}
    }
    &::-ms-track {
      height: ${TRACK_SIZE}px;
      background: transparent;
      border-color: transparent;
      color: transparent;
    }
    &::-ms-fill-lower {
      ${styles.trackStyles}
    }
    &::-ms-fill-upper {
      ${styles.trackStyles}
    }
    &::-webkit-slider-thumb {
      margin-top: -${THUMB_SIZE / 2 - TRACK_SIZE / 2}px;
      ${styles.thumbStyles}
    }
    &::-moz-range-thumb {
      ${styles.thumbStyles}
    }
    &::-ms-thumb {
      ${styles.thumbStyles}
    }
  `,
  SliderLabel: styled.label`
    font-weight: 500;
    font-size: 1.3rem;
    display: block;
    margin-bottom: 7px;
    color: ${ninja.getShade(11)}; // match vstui select label
  `,
  RangeContainer: styled.div`
    display: flex;
    align-items: center;
  `,
  IndicatorContainer: styled.div`
    color: ${cloud.getShade(9)};
    display: flex;
    align-items: center;
    &:first-child {
      margin-right: ${HALF_SPACING}px;
    }
    &:last-child > * {
      margin-left: ${HALF_SPACING}px;
    }
  `,
  IndicatorButton: styled.button`
    margin: 0;
    padding: 0;
    border: 0;
    background: none;
    display: flex;
    align-items: center;
    height: 20px;
    &:focus {
      outline: 0;
      border-radius: 2px;
      box-shadow: rgb(255, 255, 255) 0 0 0 2px,
        ${props => props.theme.vstui.color.primary.getShade(6)} 0 0 0 4px;
    }
  `,
};

const BUTTON_STEP = 0.1;

interface IndicatorProps {
  onClick: () => void;
  label: string;
}

const Indicator: FunctionComponent<IndicatorProps> = ({
  children,
  onClick,
  label,
}) => {
  return (
    <Styled.IndicatorContainer>
      <Styled.IndicatorButton
        onClick={onClick}
        type="button"
        aria-label={label}
      >
        {children}
      </Styled.IndicatorButton>
    </Styled.IndicatorContainer>
  );
};

interface SliderProps {
  value: number;
  onChange: (speed: number) => void;
}

class Slider extends Component<SliderProps & WrappedComponentProps> {
  inputId = generateUUID();

  sendFakeEvent(delta: number) {
    const value = clamp(this.props.value + delta, 0, 1);
    this.props.onChange(value);
  }

  handleRangeIncrease = () => {
    this.sendFakeEvent(BUTTON_STEP);
  };

  handleRangeDecrease = () => {
    this.sendFakeEvent(-BUTTON_STEP);
  };

  handleRangeChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.props.onChange(parseFloat(e.currentTarget.value));
  };

  render() {
    return (
      <div>
        <Styled.SliderLabel htmlFor={this.inputId}>
          <FormattedMessage id="tts.readingSpeed" />
        </Styled.SliderLabel>
        <Styled.RangeContainer>
          <Indicator
            onClick={this.handleRangeDecrease}
            label={this.props.intl.formatMessage({ id: "tts.decreaseRate" })}
          >
            <Minus />
          </Indicator>
          <Styled.RangeInput
            id={this.inputId}
            type="range"
            step={0.1}
            onChange={this.handleRangeChange}
            min={0}
            max={1}
            value={this.props.value}
          />
          <Indicator
            onClick={this.handleRangeIncrease}
            label={this.props.intl.formatMessage({ id: "tts.increaseRate" })}
          >
            <Plus />
          </Indicator>
        </Styled.RangeContainer>
      </div>
    );
  }
}

export default injectIntl(Slider);
